/**
 * A ticket the support assistant drafted (TMMAPP-0142 U2 items 13, 25d; §I): a turn that ended
 * `NEEDS_CONFIRMATION`. Nothing is filed until the person confirms.
 * - The draft is editable: its type, title (at most 120) and description (at most 4000).
 * - The files on the person's message go with it, each removable.
 * - **File it** sends `POST /v1/me/tickets {type, title, description, draftTurnId, attachmentIds}`;
 *   **Not now** stores the choice (`POST …/turns/{turnId}/ticket-draft/dismiss`, TMMAPI-0185 U1;
 *   free and idempotent), so the draft is omitted from its line and doesn't come back after a
 *   reload. A `409` (it was filed meanwhile) goes to the host (`onDismissRefused`), which reads the
 *   line again. Either way the card records the outcome, and the next message is never blocked by
 *   it.
 * - The day's limit (`429`) reads "You've filed the most tickets allowed for today…".
 * - A draft already filed (the transcript's `ticketNumber`, TMMAPP-0142 L8) reads "Filed as
 *   {number}", which opens the ticket.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { assistantApi, type AssistantAttachment, type AssistantTicketDraft } from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { isApiError, type ApiError } from '@/api/errors';
import {
  TICKET_DESCRIPTION_MAX,
  TICKET_TITLE_MAX,
  ticketsApi,
  type Ticket,
  type TicketType,
} from '@/api/tickets';
import { CloseIcon } from '@/components/icons';
import { SubmitButton } from '@/components/SubmitButton';
import { formatByteSize } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { AttachmentKindIcon } from '../composer/AttachmentFacts';
import { TICKET_TYPE_KEYS, ticketErrorCopy } from './ticketCopy';

/** A support turn's ticket draft, and what the person did with it. */
export interface DraftCard {
  turnId: number;
  draft: AssistantTicketDraft;
  outcome: 'pending' | 'filed' | 'dismissed';
  /** The filed ticket's label (`SC-1002`) and number, once filed. */
  filedAs: string | null;
  filedNumber: number | null;
}

export function TicketDraftCard({
  sessionId,
  card,
  files,
  readOnly = false,
  onFiled,
  onDismiss,
  onDismissRefused,
  onOpenTicket,
}: {
  sessionId: number;
  card: DraftCard;
  /** The files on the person's message, sent with the ticket unless removed. */
  files: readonly AssistantAttachment[];
  readOnly?: boolean | undefined;
  onFiled: (ticket: Ticket) => void;
  /** Not now was stored. */
  onDismiss: () => void;
  /** Not now's `409`: the draft was filed meanwhile (`details.reason` `TICKET_FILED`), or is gone. */
  onDismissRefused: (error: ApiError) => void;
  onOpenTicket?: ((number: number) => void) | undefined;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [type, setType] = useState<TicketType>(card.draft.type);
  const [title, setTitle] = useState(card.draft.title.slice(0, TICKET_TITLE_MAX));
  const [description, setDescription] = useState(
    card.draft.description.slice(0, TICKET_DESCRIPTION_MAX),
  );
  const [left, setLeft] = useState<ReadonlySet<number>>(new Set());
  const [filing, setFiling] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kept = files.filter((file) => !left.has(file.attachmentId));
  const ready = title.trim().length > 0 && description.trim().length > 0;
  const titleId = `ticket-draft-${card.turnId}`;

  async function file() {
    if (!ready || filing) return;
    setFiling(true);
    setError(null);
    try {
      const ticket = await ticketsApi.create({
        type,
        title: title.trim(),
        description: description.trim(),
        draftTurnId: card.turnId,
        attachmentIds: kept.map((item) => item.attachmentId),
      });
      onFiled(ticket);
    } catch (caught) {
      setError(ticketErrorCopy(t, caught, locale));
    } finally {
      setFiling(false);
    }
  }

  async function dismiss() {
    if (filing || dismissing) return;
    setDismissing(true);
    setError(null);
    try {
      await assistantApi.dismissTicketDraft(sessionId, card.turnId);
      onDismiss();
    } catch (caught) {
      if (isApiError(caught) && caught.status === 409) onDismissRefused(caught);
      else setError(errorCopy(t, caught, 'support.draft.dismissError'));
    } finally {
      setDismissing(false);
    }
  }

  const frame = {
    p: 1.5,
    borderRadius: 1.5,
    border: `1px solid ${tokens.line}`,
    bgcolor: tokens.surface,
  } as const;

  if (card.outcome !== 'pending') {
    const number = card.filedNumber;
    return (
      <Paper component="section" aria-labelledby={titleId} sx={frame} data-ticket-draft="settled">
        <Typography id={titleId} component="h3" sx={{ m: 0, fontSize: 14, fontWeight: 600 }}>
          {t('support.draft.title')}
        </Typography>
        <Typography
          tabIndex={-1}
          sx={{ fontSize: 13, color: tokens.text2, mt: 0.5 }}
          data-draft-outcome={card.outcome}
        >
          {card.outcome === 'filed'
            ? t('support.draft.filed', { number: card.filedAs ?? '' })
            : t('support.draft.dismissed')}
          {card.outcome === 'filed' && number !== null && onOpenTicket !== undefined && (
            <>
              {' '}
              <Link component="button" type="button" onClick={() => onOpenTicket(number)}>
                {t('support.draft.openTicket', { number: card.filedAs ?? '' })}
              </Link>
            </>
          )}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper component="section" aria-labelledby={titleId} sx={frame} data-ticket-draft="pending">
      <Stack sx={{ gap: 1.5 }}>
        <Box>
          <Typography id={titleId} component="h3" sx={{ m: 0, fontSize: 14, fontWeight: 600 }}>
            {t('support.draft.title')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.25 }}>
            {t('support.draft.intro')}
          </Typography>
        </Box>
        {error !== null && (
          <Alert severity="warning" data-draft-error="">
            {error}
          </Alert>
        )}
        <FormControl disabled={readOnly || filing}>
          <FormLabel id={`${titleId}-type`} sx={{ fontSize: 13 }}>
            {t('support.tickets.typeLabel')}
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby={`${titleId}-type`}
            value={type}
            onChange={(event) => setType(event.target.value as TicketType)}
          >
            {(['SUPPORT_CASE', 'FEATURE_REQUEST'] as const).map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" />}
                label={t(TICKET_TYPE_KEYS[value])}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <TextField
          size="small"
          label={t('support.tickets.titleLabel')}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          disabled={readOnly || filing}
          required
          fullWidth
          slotProps={{ htmlInput: { maxLength: TICKET_TITLE_MAX } }}
        />
        <TextField
          size="small"
          label={t('support.tickets.descriptionLabel')}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={readOnly || filing}
          required
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
          slotProps={{ htmlInput: { maxLength: TICKET_DESCRIPTION_MAX } }}
        />
        {kept.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
              {t('support.draft.files')}
            </Typography>
            <Stack
              role="list"
              aria-label={t('support.draft.files')}
              sx={{ gap: 0.5, mt: 0.5 }}
              data-draft-files=""
            >
              {kept.map((item) => (
                <Stack
                  role="listitem"
                  key={item.attachmentId}
                  direction="row"
                  sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}
                >
                  <Box sx={{ display: 'flex', color: tokens.text2 }}>
                    <AttachmentKindIcon kind={item.kind} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.name}
                    <Box component="span" sx={{ color: tokens.text2 }}>
                      {` · ${formatByteSize(item.sizeBytes, locale)}`}
                    </Box>
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={t('support.draft.removeFile', { name: item.name })}
                    disabled={readOnly || filing}
                    onClick={() => setLeft((current) => new Set([...current, item.attachmentId]))}
                    sx={{ color: tokens.text2 }}
                  >
                    <CloseIcon size={16} />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
        <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <SubmitButton
            variant="text"
            size="small"
            loading={dismissing}
            disabled={filing || readOnly}
            onClick={() => void dismiss()}
          >
            {t('support.draft.discard')}
          </SubmitButton>
          <SubmitButton
            size="small"
            loading={filing}
            disabled={!ready || readOnly || dismissing}
            onClick={() => void file()}
          >
            {t('support.draft.file')}
          </SubmitButton>
        </Stack>
      </Stack>
    </Paper>
  );
}
